import React, { ReactNode, useContext, useState } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import throttle from 'lodash/throttle'
import { useSlug, useWindowDimensions } from 'hooks'
import * as layoutConstants from 'constants/layout'
import { eventLogoSelector } from 'features/auth/eventPreviewSlice'
import { boothLogoSelector } from 'features/booth/config/fetchBoothSlice'
import BoothLogoPlaceholder from 'assets/logo-placeholder.webp'
import { colors } from 'utils'
import Breadcrumb from './Breadcrumb'
import TopNav from './topnav/TopNav'
import Sidebar from './sidebar/Sidebar'

export const TOP_NAV_HEIGHT = layoutConstants.TOP_NAV_HEIGHT
export const BREADCRUMB_HEIGHT = layoutConstants.BREADCRUMB_HEIGHT
export const MOBILE_BREAKPOINT = layoutConstants.MOBILE_BREAKPOINT

// From 1440px -> 1920px, sidebar scales from 250 -> 350
const SCALE_SIDEBAR_FACTOR = (350-250)/(1920 - 1440)
export const scaleSidebar = (screenWidth) => (
    Math.max(Math.min((screenWidth - 1440)*SCALE_SIDEBAR_FACTOR + 250, 300), 250)
)

const S = {
    Layout: styled.div`
      width: 100%;
    `,
    PageWrap: styled.div<{ screenWidth?: number }>`
        display: grid;
        grid-template-columns: 250px 1fr;
        max-height: 100vh;
        overflow: hidden;
        @media (min-width: 1441px) {
            grid-template-columns: ${({ screenWidth }) => scaleSidebar(screenWidth)}px 1fr;
        }
    `,
    Page: styled.div`
        position: relative;
        width: 100%;
        margin-top: ${TOP_NAV_HEIGHT}px;
        min-height: calc(100vh - ${TOP_NAV_HEIGHT}px);
        background: ${colors.pageBackground};
        overflow: auto;
    `,
    PageContent: styled.div<{ screenWidth?: number }>`
        height: calc(100vh - ${BREADCRUMB_HEIGHT + TOP_NAV_HEIGHT}px);
        padding: 20px;
        @media (min-width: 1231px) {
            // 20 is the fixed padding
            padding-right: ${({ screenWidth }) => Math.min(screenWidth - 1230 + 20, 230)}px;
        }
    `,
    LogoWrapper: styled.div`
      height: ${TOP_NAV_HEIGHT}px;
      overflow: hidden;
      padding: 6px;
    `,
    Logo: styled.img`
      height: 50px;
      margin: auto;
    `,
}

interface Props {
    children: ReactNode
    title?: string
    hasSpaceBreadcrumb?: boolean
    screenWidth?: number
    noBreadcrumb?: boolean
    hasBackButton?: boolean
    context?: string
}

export const PageContent = ({
    children, title, hasSpaceBreadcrumb, noBreadcrumb, hasBackButton
}: Props) => {
    const {
        screenWidth, pageHasScrolled, setPageScrolled, formHasChange
    } = useContext(LayoutContext)
    const boothSlug = useSlug('boothSlug')
    const shouldPushDownContent = formHasChange && !pageHasScrolled

    const onScroll = (e) => {
        if (e.target.scrollTop > 20 && !pageHasScrolled) {
            setPageScrolled(true)
            return
        }

        if (e.target.scrollTop === 0 && pageHasScrolled) {
            setPageScrolled(false)
        }
    }

    return (
        <>
            {boothSlug && <Sidebar isBooth />}
            <S.Page id="scrollableDiv" onScroll={throttle(onScroll, 200)}>
                {!noBreadcrumb && (
                    <Breadcrumb
                        title={title}
                        hasSpaceBreadcrumb={hasSpaceBreadcrumb}
                        hasBackButton={hasBackButton}
                        shouldPushDownContent={shouldPushDownContent}
                    />
                )}
                <S.PageContent screenWidth={screenWidth} id="page-content">
                    {children}
                </S.PageContent>
            </S.Page>
        </>
    )
}

const NormalLayout = ({
    children, screenWidth
}: Props) => {
    const logo = useSelector(eventLogoSelector)

    return (
        <S.Layout>
            <TopNav logo={logo} screenWidth={screenWidth} />
            <S.PageWrap screenWidth={screenWidth}>
                <Sidebar />
                {children}
            </S.PageWrap>
        </S.Layout>
    )
}

const BoothLayout = ({ children, screenWidth }: Props) => {
    const logo = useSelector(boothLogoSelector) || BoothLogoPlaceholder

    return (
        <S.Layout>
            <TopNav logo={logo} screenWidth={screenWidth} />
            <S.PageWrap screenWidth={screenWidth}>
                {children}
            </S.PageWrap>
        </S.Layout>
    )
}

export interface LayoutContextProps {
    screenWidth: number
    screenHeight: number
    scaledSidebar: number
    pageHasScrolled: boolean
    setPageScrolled: (e: boolean) => void
    formHasChange: boolean
    setFormChanged: (e: boolean) => void
}

export const LayoutContext = React.createContext<LayoutContextProps>(null)

const Layout = ({
    children
}: Props) => {
    const location = useLocation()
    const { width: screenWidth, height: screenHeight } = useWindowDimensions()
    const scaledSidebar = scaleSidebar(screenWidth)
    const [pageHasScrolled, setPageScrolled] = useState(false)
    const [formHasChange, setFormChanged] = useState(false)

    return (
        <LayoutContext.Provider
            value={{
                screenWidth,
                screenHeight,
                scaledSidebar,
                pageHasScrolled,
                setPageScrolled,
                formHasChange,
                setFormChanged
            }}
        >
            {
                location.pathname.includes('/booth/') ? (
                    <BoothLayout screenWidth={screenWidth}>
                        {children}
                    </BoothLayout>
                ) : (
                    <NormalLayout
                        screenWidth={screenWidth}
                    >
                        {children}
                    </NormalLayout>
                )
            }
        </LayoutContext.Provider>
    )
}

export default Layout
